import type { StorefrontHighlight, StorefrontStory } from '@erp/shared';
import { Bookmark } from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { assetUrl, publicApi } from '../../lib/publicApi.js';
import { isSeen, useStoriesSeen } from '../../lib/storiesSeen.js';

import { StoryViewer } from './StoryViewer.js';

// Instagram-style story rings for the storefront home. Two instances render from one
// shared fetch: kind="stories" (every live story, unseen first — highlight
// membership does not hide one) and kind="highlights" (أبرز اللحظات — pinned
// collections, bookmark badge).
// The `stories` module returns 403 when disabled — treated as "no stories", so the
// tray simply doesn't render (no flag on StorefrontInfo by design).

interface StoriesData {
  stories: StorefrontStory[];
  highlights: StorefrontHighlight[];
}

// One in-flight/settled fetch per slug+locale shared by both tray instances.
// Entries expire so stories that go live / expire mid-session show up on the
// next home visit without a hard reload.
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { p: Promise<StoriesData>; at: number }>();

function loadStories(slug: string, locale: string): Promise<StoriesData> {
  const key = `${slug}:${locale}`;
  const hit = cache.get(key);
  if (hit && performance.now() - hit.at < CACHE_TTL_MS) return hit.p;
  const p = Promise.all([
    publicApi().get<{ items: StorefrontStory[] }>(`/${slug}/stories`).then((r) => r.data.items),
    publicApi().get<{ items: StorefrontHighlight[] }>(`/${slug}/highlights`).then((r) => r.data.items),
  ])
    .then(([stories, highlights]) => ({ stories, highlights }))
    .catch(() => ({ stories: [], highlights: [] }));
  cache.set(key, { p, at: performance.now() });
  // Let a transient failure retry on the next mount instead of caching emptiness for a minute.
  void p.then((d) => { if (d.stories.length === 0 && d.highlights.length === 0) cache.delete(key); });
  return p;
}

interface RingProps {
  label: string;
  coverUrl: string | null;
  unseen: boolean;
  pinned?: boolean;
  delayMs: number;
  onClick: () => void;
}

function Ring({ label, coverUrl, unseen, pinned, delayMs, onClick }: RingProps): JSX.Element {
  const img = assetUrl(coverUrl);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ['--sf-delay']: `${delayMs}ms` } as CSSProperties}
      className="sf-card group flex w-[76px] shrink-0 flex-col items-center gap-1.5 outline-none"
    >
      <span className={`relative grid place-items-center rounded-full p-[3px] transition group-hover:scale-105 motion-reduce:transform-none ${unseen ? 'bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600' : 'bg-border'}`}>
        <span className="grid h-16 w-16 place-items-center overflow-hidden rounded-full border-2 border-bg bg-bg-subtle">
          {img ? (
            <img src={img} alt={label} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-fg-subtle">{label.slice(0, 1)}</span>
          )}
        </span>
        {pinned && (
          <span className="absolute -bottom-0.5 -end-0.5 grid h-5 w-5 place-items-center rounded-full border-2 border-bg bg-brand text-brand-contrast">
            <Bookmark className="h-2.5 w-2.5" aria-hidden />
          </span>
        )}
      </span>
      <span className="w-full truncate text-center text-xs text-fg-muted">{label}</span>
    </button>
  );
}

export function StoriesTray({ slug, kind }: { slug: string; kind: 'stories' | 'highlights' }): JSX.Element | null {
  const { t, i18n } = useTranslation('cms');
  const [data, setData] = useState<StoriesData | null>(null);
  const seenState = useStoriesSeen();
  const [viewer, setViewer] = useState<{ playlist: StorefrontStory[]; startId: string } | null>(null);

  useEffect(() => {
    let live = true;
    void loadStories(slug, i18n.language).then((d) => { if (live) setData(d); });
    return () => { live = false; };
  }, [slug, i18n.language]);

  const rows = useMemo(() => {
    if (!data) return null;
    if (kind === 'highlights') {
      // A highlight owns its frames now, so it is played as a ONE-entry playlist
      // carrying its own media rather than a lookup into the stories list. A
      // link-only highlight has no frames; the server already omits any that has
      // neither, so nothing here needs to filter it out again.
      return data.highlights.map((h) => ({
        key: h.id,
        label: h.title,
        coverUrl: h.coverUrl,
        playlist: [{
          id: h.id,
          title: h.title,
          caption: null,
          coverUrl: h.coverUrl,
          media: h.media,
          products: [],
          link: h.link,
        } as StorefrontStory],
        pinned: true,
      }));
    }
    // Stories row: every live story, unseen first. Highlight membership does
    // NOT hide a story here — a highlight is an archive shelf, and hiding its
    // members emptied the whole row the moment the shop filed everything.
    const unseenFirst = [...data.stories].sort((a, b) => Number(isSeen(seenState, slug, a.id)) - Number(isSeen(seenState, slug, b.id)));
    return unseenFirst.map((s) => ({
      key: s.id,
      label: s.title ?? t('store.stories.story', { defaultValue: 'Story' }),
      coverUrl: s.coverUrl,
      // Play from the tapped story through the rest of the row, like the app.
      playlist: unseenFirst,
      pinned: false,
    }));
  }, [data, kind, seenState, slug, t]);

  if (!rows || rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-2" aria-label={kind === 'highlights' ? t('store.stories.highlights', { defaultValue: 'Highlights' }) : t('store.stories.title', { defaultValue: 'Stories' })}>
      <h2 className="text-sm font-semibold text-fg-muted">
        {kind === 'highlights'
          ? t('store.stories.highlights', { defaultValue: 'Highlights' })
          : t('store.stories.title', { defaultValue: 'Stories' })}
      </h2>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" style={{ scrollbarWidth: 'none' }}>
        {rows.map((r, i) => {
          const allSeen = r.playlist.every((s) => isSeen(seenState, slug, s.id));
          return (
            <Ring
              key={r.key}
              label={r.label}
              coverUrl={r.coverUrl}
              unseen={!allSeen}
              pinned={r.pinned}
              delayMs={Math.min(i, 10) * 40}
              onClick={() => setViewer({ playlist: r.playlist, startId: kind === 'highlights' ? r.playlist[0]!.id : r.key })}
            />
          );
        })}
      </div>
      {viewer && (
        <StoryViewer slug={slug} stories={viewer.playlist} startId={viewer.startId} onClose={() => setViewer(null)} />
      )}
    </section>
  );
}
